import { useContext, useRef } from 'react';
import { useCloudinaryResize } from '@socialchorus/shared-ui-components';
import {
  BannerChannelSettings,
  BannerHomepageModule,
  BannerModuleSettings,
} from '../../models/home-page-custom/types';
import { useLinkClickHandler } from '../../hooks/useLinkClickHandler';
import { trackBannerImageClick } from '../../models/analytics';
import { CustomHomepageIdContext } from './custom-homepage-id-context';

// FE-32789: mobile viewport shows the mobile channel only when it's enabled
// (Kai guarantees an enabled mobile channel carries an image — its own or the
// web image copied in). A disabled mobile channel renders nothing — no web
// fallback. Desktop shows the web channel when enabled.
const resolveChannel = (
  settings: BannerModuleSettings | undefined,
  useMobileBanner: boolean
): BannerChannelSettings | null => {
  if (useMobileBanner) {
    const mobile = settings?.mobile_settings;
    return mobile && mobile.enabled !== false ? mobile : null;
  }
  const web = settings?.web_settings;
  return web && web.enabled !== false ? web : null;
};

// Cloudinary `ar_` values ("8:1") map directly to the CSS `aspect-ratio`
// shorthand ("8 / 1"). The server is the single source of truth for the ratio.
const cssAspectRatio = (ratio?: string): string | undefined =>
  ratio?.replace(':', ' / ');

export const HomepageBanner = ({
  bannerModule,
  useMobileBanner = false,
}: {
  bannerModule: BannerHomepageModule;
  useMobileBanner?: boolean;
}) => {
  const homepageId = useContext(CustomHomepageIdContext);
  const channel = resolveChannel(
    bannerModule?.attributes.settings,
    useMobileBanner
  );

  const { handleClick, handleKeyDown, normalizedUrl } = useLinkClickHandler(
    channel?.image_url
  );

  const imageRef = useRef<HTMLImageElement>(null);
  const resizedSrc = useCloudinaryResize({
    imageRef,
    originalImgSrc: channel?.image,
    aspectRatio: channel?.aspect_ratio,
  });

  if (!channel?.image) return null;

  // FE-33340: record banner clicks for the Intelligence team.
  const trackClick = () =>
    trackBannerImageClick({
      ...(homepageId ? { homepage_id: homepageId } : {}),
      module_id: bannerModule.id,
      image_url: channel.image_url ?? '',
      image_file_name: channel.image ?? '',
      aria_label: channel.aria_label ?? '',
      location: useMobileBanner ? 'mobile_web' : 'web',
    });

  const style = { aspectRatio: cssAspectRatio(channel.aspect_ratio) };
  const bannerContent = (
    <img
      ref={imageRef}
      src={resizedSrc ?? channel.image}
      alt={channel.aria_label ?? ''}
    />
  );

  // Render as a link whenever there's a destination URL. We deliberately do not
  // also gate on the ARIA label: it's mandatory on the admin side, so a missing
  // one is an authoring-data problem to surface there — not a reason to silently
  // drop a working homepage link for the (mostly sighted) audience. When the
  // label is missing the image alt is empty, so name the link itself from the
  // module name as a fallback to avoid an unnamed link (WCAG 4.1.2).
  if (normalizedUrl) {
    return (
      <a
        href={normalizedUrl}
        className="home__banner"
        style={style}
        aria-label={
          channel.aria_label ? undefined : bannerModule?.attributes.name
        }
        // Track only here, not in onKeyDown: an anchor fires a synthesized
        // click on Enter activation, so onClick already covers keyboard use.
        // Adding a keydown tracker would double-count (matches ContentLink).
        onClick={(e) => {
          trackClick();
          handleClick(e);
        }}
        onKeyDown={handleKeyDown}
      >
        {bannerContent}
      </a>
    );
  }

  // Otherwise, render as a div: there is no destination to navigate to.
  return (
    <div className="home__banner" style={style}>
      {bannerContent}
    </div>
  );
};
